import React, { useEffect, useMemo, useState } from 'react';
import { Tree, theme, Spin } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { BPRCProcess } from 'store/ducks/Reporting/types';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import * as Styled from '../../Layouts/Layout.styled';
import * as LocalStyled from 'pages/Reporting/ControlReport/ControlReport.styled';
import { buildOrgTree, buildTreeData } from './buildProcessRiskControlTree';
import { useTranslation } from 'react-i18next';

const { useToken } = theme;

const OrgHierarchyProcessRiskControlTree: React.FC = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);

  const [processes, setProcesses] = useState<BPRCProcess[]>([]);
  const [loading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    if (!shouldFetch) return;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await ApiService.getBusinessProcessRiskControlReport();
        if (
          response.data?.status === 'success' &&
          response.data?.data?.business_process_l4
        ) {
          setProcesses(response.data.data.business_process_l4);
        } else {
          setProcesses([]);
        }
      } catch (error) {
        console.error(error);
        setProcesses([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchReport();
  }, [shouldFetch]);

  const treeData: DataNode[] = useMemo(() => {
    if (!processes.length) return [];
    return buildTreeData(buildOrgTree(processes), token);
  }, [processes, token]);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={t('PROCESS_RISK_CONTROL')}
        goBackButtons={
          <GoBackButton to='/dependency_bpr' text={t('GO_BACK')} />
        }
      />
      <Styled.HorizontalLayoutMain theme={scheme}>
        <Styled.Container>
          <LocalStyled.HierarchyLevels>
            <Styled.StyledButton
              type='primary'
              onClick={() => setShouldFetch(true)}
              className='display-save-button'
            >
              {t('DISPLAY')}
            </Styled.StyledButton>
          </LocalStyled.HierarchyLevels>

          <Styled.ContainerDependency token={token}>
            <Styled.CardItemWrapper>
              {!loading && processes.length === 0 ? (
                <LocalStyled.EmptyState>
                  {t('common.noData')}
                </LocalStyled.EmptyState>
              ) : (
                <Spin spinning={loading}>
                  <Tree treeData={treeData} showLine />
                </Spin>
              )}
            </Styled.CardItemWrapper>
          </Styled.ContainerDependency>
        </Styled.Container>
      </Styled.HorizontalLayoutMain>
    </Styled.StyledLayout>
  );
};

export default OrgHierarchyProcessRiskControlTree;
